// Shared plan/pricing definitions. The brochure pricing page reads
// from this. The admin panel uses these as the starting state for
// the Subscriptions editor and can persist overrides via the
// `/admin/plans` endpoint (currently localStorage in dev).
import type { TierId } from './capabilities'

// A plan is what someone buys; a tier is what the capability matrix is keyed
// on. They were the same thing until Pro+ arrived. Pro+ is Pro's feature set
// with a larger managed-AI allowance and Opus, so it has its own plan (price,
// card, Stripe price) but resolves to the 'pro' column of the matrix. Keeping
// the matrix at three columns means no snapshot of it (server, desktop, admin)
// has to grow a fourth column that would only ever repeat Pro's.
export type PlanId = TierId | 'proplus'

/** The capability-matrix column a plan reads its feature set from. */
export function capabilityTier(plan: PlanId): TierId {
  return plan === 'proplus' ? 'pro' : plan
}

/**
 * Managed-AI model families, cheapest first. Bring-your-own-key is open on
 * every plan and is not limited by this; this is what Plexii's own key will
 * run for the plan.
 */
export type AiModelFamily = 'haiku' | 'sonnet' | 'opus'

/**
 * One-off AI credit packs. 1 credit = US$0.01 of billed usage. Smaller packs
 * are not offered: card fees make them uneconomic.
 */
export const CREDIT_PACKS: ReadonlyArray<{ usd: number; credits: number }> = [
  { usd: 10, credits: 1000 },
  { usd: 25, credits: 2600 },
  { usd: 50, credits: 5500 }
]

export interface Plan {
  id: PlanId
  name: string
  // Marketing tagline shown on the brochure card.
  tagline: string
  // Long-form pitch for the brochure card body.
  pitch: string
  priceMonthly: number
  priceYearly: number
  // What "1 seat" means for billing. Free/Pro are per-user; Team is per-seat in a workspace.
  unit: 'user' | 'seat'
  // Sort order on the brochure.
  order: number
  // Highlighted as the recommended plan on the brochure.
  highlighted: boolean
  // Calls-to-action.
  ctaPrimary: { label: string; href: string }
  ctaSecondary?: { label: string; href: string }
  // Plain-text bullets that summarise the value (separate from the
  // capability matrix). The matrix is exhaustive; these are the
  // headline 4-6 that go on the plan card.
  headlineBullets: string[]
  // Active = appears on brochure + admin allows new signups.
  active: boolean
  // Managed-AI credits granted each month (1 credit = US$0.01 of billed
  // usage). Team is per seat, pooled across the organisation.
  aiCreditsMonthly: number
  // Model families managed AI will run on this plan.
  aiModels: AiModelFamily[]
}

export const PLANS: Plan[] = [
  {
    id: 'free',
    name: 'Free',
    tagline: 'The complete personal workspace.',
    pitch: 'All four apps, up to three desks in as many rooms as you like, and 100 AI credits a month on Haiku, or PlexiiBrain on your own key. Local-first and yours to keep, syncing across up to three devices.',
    priceMonthly: 0,
    priceYearly: 0,
    unit: 'user',
    order: 0,
    highlighted: false,
    ctaPrimary: { label: 'Download PlexiiDesk', href: '/download' },
    headlineBullets: [
      'All four apps, PlexiiDesk, PlexiiOffice, PlexiiBrain and PlexiiTeam',
      'Unlimited desks and unlimited tasks per desk',
      '100 AI credits a month on Haiku, or bring your own Anthropic key',
      'Voice command, voice and video recorder, cloud Whisper',
      'Local-first storage with a master-password vault',
      'Sync across up to three devices'
    ],
    active: true,
    aiCreditsMonthly: 100,
    aiModels: ['haiku']
  },
  {
    id: 'pro',
    name: 'Pro',
    tagline: 'Managed AI, backups and every device.',
    pitch: 'Let us run PlexiiBrain for you, keep your work backed up, and sync across every device. 800 AI credits a month on Haiku and Sonnet with no key to set up, cloud version history, AI email replies, e-signatures and marketplace credits.',
    priceMonthly: 12,
    priceYearly: 120,
    unit: 'user',
    order: 1,
    highlighted: true,
    ctaPrimary: { label: 'Upgrade to Pro', href: '/account/signup?plan=pro' },
    ctaSecondary: { label: '14-day trial in app', href: '/download' },
    headlineBullets: [
      'Everything in Free, plus',
      '800 AI credits a month on Haiku and Sonnet, no API key to set up',
      'Unlimited device sync and cloud workspace version history',
      'AI email reply drafts and PlexiiSign e-signatures',
      'Vault backup and restore',
      'Marketplace credits, and publish your own'
    ],
    active: true,
    aiCreditsMonthly: 800,
    aiModels: ['haiku', 'sonnet']
  },
  {
    id: 'proplus',
    name: 'Pro+',
    tagline: 'Pro with more AI, and Opus.',
    pitch: 'Everything in Pro, with 2,500 AI credits a month and Opus alongside Haiku and Sonnet, for people who run PlexiiBrain all day.',
    priceMonthly: 25,
    priceYearly: 250,
    unit: 'user',
    order: 2,
    highlighted: false,
    ctaPrimary: { label: 'Upgrade to Pro+', href: '/account/signup?plan=proplus' },
    ctaSecondary: { label: '14-day trial in app', href: '/download' },
    headlineBullets: [
      'Everything in Pro, plus',
      '2,500 AI credits a month',
      'Opus, alongside Haiku and Sonnet',
      'Longer AI answers per request'
    ],
    active: true,
    aiCreditsMonthly: 2500,
    aiModels: ['haiku', 'sonnet', 'opus']
  },
  {
    id: 'team',
    name: 'Team',
    tagline: 'Your whole team, together.',
    pitch: 'The People Map and organisation directory, assign desks to teammates, push universal decks, roles and a central admin console with per-seat analytics and single sign-on. 800 AI credits per seat a month, pooled across the team. Billed per active seat.',
    priceMonthly: 15,
    priceYearly: 150,
    unit: 'seat',
    order: 3,
    highlighted: false,
    ctaPrimary: { label: 'Start a team workspace', href: '/account/signup?plan=team' },
    ctaSecondary: { label: 'Talk to us', href: '/contact' },
    headlineBullets: [
      'Everything in Pro, plus',
      'The People Map, organisation directory and multiple organisations',
      'Assign desks to teammates and push universal decks',
      'Roles, a centralised admin console and per-seat usage analytics',
      'Single sign-on and directory sync (SCIM)',
      '800 AI credits per seat a month, pooled across the team',
      'Priority support, billed only for active seats'
    ],
    active: true,
    aiCreditsMonthly: 800,
    aiModels: ['haiku', 'sonnet']
  }
]

export function planById(id: PlanId): Plan | undefined {
  return PLANS.find((p) => p.id === id)
}

export function formatPrice(n: number): string {
  if (n === 0) return '$0'
  // Round to whole cents so binary float noise never surfaces (e.g. a savings
  // calc like 9.95 * 12 - 99 is 20.400000000000006, which must read "$20.40"),
  // and drop a trailing .00 so a whole-dollar price reads "$99", not "$99.00".
  const cents = Math.round(n * 100)
  return cents % 100 === 0 ? `$${cents / 100}` : `$${(cents / 100).toFixed(2)}`
}
